import { Link, useNavigate, useParams } from "react-router";
import { IconChevronLeft } from "@tabler/icons-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useBoat } from "@/api/hooks/use-boats";
import { useWingWeighings } from "@/api/hooks/use-wing-weighings";
import { useWingWeighingActions } from "@/hooks/use-wing-weighing-actions";
import { WingWeighingForm } from "@/components/wing-weighing-form";
import { saveWeighingThenMaybeSignOff } from "@/lib/weighing-save";
import { WeighingStateBadge } from "@/components/weighing-state-badge";
import { asBoatId, type WingWeighingId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type WingWeighingCreate = components["schemas"]["WingWeighingCreate"];
type WingWeighingUpdate = components["schemas"]["WingWeighingUpdate"];

export function WingWeighingPage() {
  const params = useParams();
  const navigate = useNavigate();
  const boatId = asBoatId(params.boatId ?? "");
  const weighingId = params.weighingId;

  const boatQuery = useBoat(boatId);
  const listQuery = useWingWeighings(boatId);
  const actions = useWingWeighingActions(boatId, listQuery.data);

  const backTo = `/boats/${boatId}/weighings?tab=wing`;
  const isNew = !weighingId;
  const weighing = isNew ? undefined : listQuery.data?.find((w) => w.id === weighingId);
  const boatCode = boatQuery.data?.boat_code ?? "—";

  if (listQuery.isLoading || boatQuery.isLoading) {
    return (
      <div className="max-w-3xl mx-auto">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-96 w-full rounded-lg" />
      </div>
    );
  }

  if (!isNew && !weighing) {
    return (
      <div className="max-w-3xl mx-auto">
        <p className="text-sm text-muted-foreground mb-3">Weighing not found.</p>
        <Link to={backTo} className="text-sm text-foreground underline">
          Back to {boatCode} weighings
        </Link>
      </div>
    );
  }

  const readOnly = !!weighing && weighing.state !== "draft";

  const handleSave = async (
    payload: WingWeighingCreate | WingWeighingUpdate | null,
    withSignOff: boolean,
  ) => {
    await saveWeighingThenMaybeSignOff({
      payload,
      withSignOff,
      existingId: weighing?.id as WingWeighingId | undefined,
      update: actions.handleUpdate,
      create: actions.handleCreate,
      signOffAsync: actions.handleSignOffAsync,
    });
  };

  return (
    <div className="max-w-3xl lg:max-w-5xl mx-auto">
      <div className="mb-4">
        <Link
          to={backTo}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2 w-fit"
        >
          <IconChevronLeft className="w-3 h-3" /> {boatCode} weighings
        </Link>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">
            {isNew ? "New wing weighing" : "Wing weighing"} — {boatCode}
          </h1>
          {weighing && (
            <>
              <span className="text-[10px] font-semibold tracking-wider uppercase bg-muted px-2 py-0.5 rounded">
                {weighing.config}
              </span>
              <WeighingStateBadge state={weighing.state} />
            </>
          )}
        </div>
        {weighing && (
          <p className="text-sm text-muted-foreground">
            Measured {new Date(weighing.measured_at).toLocaleDateString()} by {weighing.measured_by}
          </p>
        )}
      </div>

      <WingWeighingForm
        key={weighing?.id ?? "new"}
        boatId={boatId}
        initialData={weighing}
        latestWeighingId={listQuery.data?.[0]?.id ?? null}
        readOnly={readOnly}
        isSubmitting={actions.isCreating || actions.isUpdating}
        isSigningOff={actions.isSigningOff}
        onSave={handleSave}
        onDone={() => navigate(backTo)}
        onUnlock={
          weighing?.state === "signed_off"
            ? () => {
                if (window.confirm("Unlock for editing? This will move it back to draft.")) {
                  actions.handleUnlock(weighing.id as WingWeighingId);
                }
              }
            : undefined
        }
        isUnlocking={actions.isUnlocking}
      />
    </div>
  );
}
